import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { getToken } from "../../utilities/authHelper";

const DEFAULT_FILTERS = Object.freeze({
  tickerId: "",
  symbolValue: "",
  subscriptionType: "",
  assetClass: "",
  country: "",
  category: "",
  isActive: "",
});

const DEFAULT_TICKER_FORM = Object.freeze({
  symbol_value: "",
  label: "",
  asset_class: "",
  country: "",
  category: "",
  yahoo_ticker: "",
  supported_subscription_types_text: "",
  sort_order: "",
  is_active: true,
  udf1: "",
  udf2: "",
  udf3: "",
  udf4: "",
});

const DEFAULT_BULK_FORM = Object.freeze({
  tickerIdsText: "",
  symbolValuesText: "",
  assetClass: "",
  country: "",
  category: "",
  isActive: "",
  addSubscriptionTypesText: "",
  removeSubscriptionTypesText: "",
});

const DEFAULT_PREVIEW_FORM = Object.freeze({
  userId: "",
  subscriptionTypesText: "",
});

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const getAuthHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getToken()}`,
});

const buildApiUrl = (path) => {
  const baseUrl = String(process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  return `${baseUrl}${path}`;
};

const parseApiResponse = async (response, fallbackMessage) => {
  const payload = await response.json().catch(() => null);

  if (!response.ok || payload?.success === false) {
    throw new Error(
      payload?.errorObject?.message || payload?.message || fallbackMessage,
    );
  }

  return payload?.results || {};
};

const splitListInput = (value) =>
  Array.from(
    new Set(
      String(value || "")
        .split(/[\n,]+/)
        .map((entry) => entry.trim())
        .filter(Boolean),
    ),
  );

const normalizeRequiredText = (value) => String(value || "").trim();

const normalizeOptionalText = (value) => {
  const trimmed = String(value || "").trim();
  return trimmed ? trimmed : null;
};

const formatDateTime = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const formatAssetLabel = (value) =>
  String(value || "")
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const mapTickerToForm = (ticker) => ({
  symbol_value: ticker?.symbol_value || "",
  label: ticker?.label || "",
  asset_class: ticker?.asset_class || "",
  country: ticker?.country || "",
  category: ticker?.category || "",
  yahoo_ticker: ticker?.yahoo_ticker || "",
  supported_subscription_types_text: Array.isArray(
    ticker?.supported_subscription_types,
  )
    ? ticker.supported_subscription_types.join(", ")
    : "",
  sort_order:
    ticker?.sort_order === 0 || ticker?.sort_order
      ? String(ticker.sort_order)
      : "",
  is_active: Boolean(ticker?.is_active),
  udf1: ticker?.udf1 || "",
  udf2: ticker?.udf2 || "",
  udf3: ticker?.udf3 || "",
  udf4: ticker?.udf4 || "",
});

const buildTickerPayload = (formState) => ({
  symbol_value: normalizeRequiredText(formState.symbol_value),
  label: normalizeRequiredText(formState.label),
  asset_class: normalizeRequiredText(formState.asset_class),
  country: normalizeRequiredText(formState.country),
  category: normalizeRequiredText(formState.category),
  yahoo_ticker: normalizeRequiredText(formState.yahoo_ticker),
  supported_subscription_types: splitListInput(
    formState.supported_subscription_types_text,
  ),
  sort_order:
    String(formState.sort_order || "").trim() === ""
      ? null
      : Number(formState.sort_order),
  is_active: Boolean(formState.is_active),
  udf1: normalizeOptionalText(formState.udf1),
  udf2: normalizeOptionalText(formState.udf2),
  udf3: normalizeOptionalText(formState.udf3),
  udf4: normalizeOptionalText(formState.udf4),
});

const countPreviewOptions = (node) => {
  if (Array.isArray(node)) {
    return node.length;
  }

  if (!node || typeof node !== "object") {
    return 0;
  }

  return Object.values(node).reduce(
    (total, value) => total + countPreviewOptions(value),
    0,
  );
};

const fetchTickerUniverseItems = async ({ filters, limit, offset }) => {
  const url = new URL(buildApiUrl("/admin/get_ticker_universe_items"));

  if (filters.tickerId) {
    url.searchParams.set("ticker_id", filters.tickerId.trim());
  }
  if (filters.symbolValue) {
    url.searchParams.set("symbol_value", filters.symbolValue.trim());
  }
  if (filters.subscriptionType) {
    url.searchParams.set("subscription_type", filters.subscriptionType.trim());
  }
  if (filters.assetClass) {
    url.searchParams.set("asset_class", filters.assetClass.trim());
  }
  if (filters.country) {
    url.searchParams.set("country", filters.country.trim());
  }
  if (filters.category) {
    url.searchParams.set("category", filters.category.trim());
  }
  if (filters.isActive !== "") {
    url.searchParams.set("is_active", filters.isActive);
  }

  url.searchParams.set("limit", String(limit));
  url.searchParams.set("offset", String(offset));

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: getAuthHeaders(),
  });

  return parseApiResponse(
    response,
    "Unable to load ticker universe items.",
  );
};

const createOrUpdateTickerUniverseItem = async ({ tickerId, payload }) => {
  const url = new URL(buildApiUrl("/admin/create_or_update_ticker_universe_item"));

  if (tickerId) {
    url.searchParams.set("ticker_id", tickerId);
  }

  const response = await fetch(url.toString(), {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  return parseApiResponse(response, "Unable to save ticker universe item.");
};

const setTickerUniverseItemStatus = async ({ tickerId, symbolValue, isActive }) => {
  const requestBody = {
    is_active: Boolean(isActive),
  };

  if (tickerId) {
    requestBody.ticker_id = tickerId;
  } else if (symbolValue) {
    requestBody.symbol_value = symbolValue;
  }

  const response = await fetch(buildApiUrl("/admin/set_ticker_universe_item_status"), {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(requestBody),
  });

  return parseApiResponse(response, "Unable to update ticker status.");
};

const disableTickerUniverseItem = async ({ tickerId, symbolValue }) => {
  const requestBody = {};

  if (tickerId) {
    requestBody.ticker_id = tickerId;
  } else if (symbolValue) {
    requestBody.symbol_value = symbolValue;
  }

  const response = await fetch(buildApiUrl("/admin/disable_ticker_universe_item"), {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(requestBody),
  });

  return parseApiResponse(response, "Unable to disable ticker.");
};

const bulkUpdateTickerSubscriptions = async (payload) => {
  const response = await fetch(buildApiUrl("/admin/bulk_update_ticker_subscriptions"), {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  return parseApiResponse(
    response,
    "Unable to bulk update ticker subscriptions.",
  );
};

const previewTickerUniverse = async ({ userId, subscriptionTypes }) => {
  const url = new URL(buildApiUrl("/admin/preview_ticker_universe"));

  if (subscriptionTypes.length > 0) {
    subscriptionTypes.forEach((subscriptionType) => {
      url.searchParams.append("subscription_type", subscriptionType);
    });
  } else if (userId) {
    url.searchParams.set("user_id", userId.trim());
  }

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: getAuthHeaders(),
  });

  return parseApiResponse(response, "Unable to preview ticker universe.");
};

const fetchSubscriptionCatalog = async () => {
  const response = await fetch(buildApiUrl("/admin/get_subscriptions"), {
    method: "GET",
    headers: getAuthHeaders(),
  });

  return parseApiResponse(response, "Unable to load subscriptions.");
};

const LABEL_CLASSNAME =
  "text-uppercase text-muted fw-semibold mb-1";

const LABEL_STYLE = {
  fontSize: "0.72rem",
  letterSpacing: "0.04em",
};

const INPUT_CLASSNAME = "border-1";

const FieldLabel = ({ children, required = false }) => (
  <Form.Label className={LABEL_CLASSNAME} style={LABEL_STYLE}>
    {children}
    {required ? <span className="text-danger ms-1">*</span> : null}
  </Form.Label>
);

const StatPill = ({ label, value }) => (
  <div
    className="rounded-3 border px-3 py-2 bg-light"
    style={{ minWidth: "9rem" }}
  >
    <div className="text-muted" style={{ fontSize: "0.72rem" }}>
      {label}
    </div>
    <div className="fw-semibold">{value}</div>
  </div>
);

const SubscriptionBadges = ({ values }) => {
  if (!Array.isArray(values) || values.length === 0) {
    return <span className="text-muted">—</span>;
  }

  return (
    <div className="d-flex flex-wrap gap-1">
      {values.map((value) => (
        <Badge key={value} bg="light" text="dark" className="border">
          {value}
        </Badge>
      ))}
    </div>
  );
};

function TickerManagement() {
  const [draftFilters, setDraftFilters] = useState({ ...DEFAULT_FILTERS });
  const [appliedFilters, setAppliedFilters] = useState({ ...DEFAULT_FILTERS });
  const [tickerItems, setTickerItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [subscriptionCatalog, setSubscriptionCatalog] = useState([]);

  const [showEditorModal, setShowEditorModal] = useState(false);
  const [editorMode, setEditorMode] = useState("create");
  const [selectedTicker, setSelectedTicker] = useState(null);
  const [tickerForm, setTickerForm] = useState({ ...DEFAULT_TICKER_FORM });
  const [savingTicker, setSavingTicker] = useState(false);

  const [rowActionKey, setRowActionKey] = useState("");

  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkForm, setBulkForm] = useState({ ...DEFAULT_BULK_FORM });
  const [savingBulkUpdate, setSavingBulkUpdate] = useState(false);

  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewForm, setPreviewForm] = useState({ ...DEFAULT_PREVIEW_FORM });
  const [previewResults, setPreviewResults] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const tableStart = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const tableEnd = Math.min(currentPage * pageSize, totalCount);
  const activeFilterCount = Object.values(appliedFilters).filter(
    (value) => String(value || "").trim() !== "",
  ).length;

  const knownSubscriptionTypes = useMemo(() => {
    const uniqueTypes = new Set();

    subscriptionCatalog.forEach((subscription) => {
      if (subscription?.subscription_type) {
        uniqueTypes.add(subscription.subscription_type);
      }
    });

    tickerItems.forEach((ticker) => {
      (ticker.supported_subscription_types || []).forEach((subscriptionType) =>
        uniqueTypes.add(subscriptionType),
      );
    });

    return Array.from(uniqueTypes).sort((left, right) =>
      left.localeCompare(right),
    );
  }, [subscriptionCatalog, tickerItems]);

  const assetClassSuggestions = useMemo(
    () =>
      Array.from(
        new Set(
          tickerItems
            .map((ticker) => ticker.asset_class)
            .filter(Boolean),
        ),
      ).sort((left, right) => left.localeCompare(right)),
    [tickerItems],
  );

  const countrySuggestions = useMemo(
    () =>
      Array.from(
        new Set(
          tickerItems
            .map((ticker) => ticker.country)
            .filter(Boolean),
        ),
      ).sort((left, right) => left.localeCompare(right)),
    [tickerItems],
  );

  const categorySuggestions = useMemo(
    () =>
      Array.from(
        new Set(
          tickerItems
            .map((ticker) => ticker.category)
            .filter(Boolean),
        ),
      ).sort((left, right) => left.localeCompare(right)),
    [tickerItems],
  );

  const parsedEditorSubscriptionTypes = useMemo(
    () => splitListInput(tickerForm.supported_subscription_types_text),
    [tickerForm.supported_subscription_types_text],
  );

  const parsedBulkAddSubscriptionTypes = useMemo(
    () => splitListInput(bulkForm.addSubscriptionTypesText),
    [bulkForm.addSubscriptionTypesText],
  );

  const parsedBulkRemoveSubscriptionTypes = useMemo(
    () => splitListInput(bulkForm.removeSubscriptionTypesText),
    [bulkForm.removeSubscriptionTypesText],
  );

  const parsedPreviewSubscriptionTypes = useMemo(
    () => splitListInput(previewForm.subscriptionTypesText),
    [previewForm.subscriptionTypesText],
  );

  const previewAssetCounts = useMemo(() => {
    const options = previewResults?.ticker_universe?.options;
    if (!options || typeof options !== "object") {
      return [];
    }

    return Object.entries(options).map(([assetType, assetOptions]) => ({
      assetType,
      count: countPreviewOptions(assetOptions),
    }));
  }, [previewResults]);

  const loadTickers = useCallback(async () => {
    setLoading(true);

    try {
      const results = await fetchTickerUniverseItems({
        filters: appliedFilters,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
      });

      setTickerItems(results.ticker_universe_items || []);
      setTotalCount(Number(results.count || 0));
    } catch (error) {
      setFeedback({
        variant: "danger",
        message: error.message || "Unable to load ticker universe items.",
      });
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, currentPage, pageSize]);

  useEffect(() => {
    loadTickers();
  }, [loadTickers]);

  useEffect(() => {
    let ignore = false;

    const loadSubscriptions = async () => {
      try {
        const results = await fetchSubscriptionCatalog();
        if (!ignore) {
          setSubscriptionCatalog(results.subscriptions_details || []);
        }
      } catch (error) {
        if (!ignore) {
          console.error("Unable to load subscription catalog:", error);
        }
      }
    };

    loadSubscriptions();

    return () => {
      ignore = true;
    };
  }, []);

  const updateDraftFilter = (field, value) => {
    setDraftFilters((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const updateTickerForm = (field, value) => {
    setTickerForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const updateBulkForm = (field, value) => {
    setBulkForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const updatePreviewForm = (field, value) => {
    setPreviewForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const applyFilters = () => {
    setCurrentPage(1);
    setAppliedFilters({ ...draftFilters });
  };

  const resetFilters = () => {
    const resetState = { ...DEFAULT_FILTERS };
    setDraftFilters(resetState);
    setAppliedFilters(resetState);
    setCurrentPage(1);
  };

  const openCreateModal = () => {
    setEditorMode("create");
    setSelectedTicker(null);
    setTickerForm({ ...DEFAULT_TICKER_FORM });
    setShowEditorModal(true);
  };

  const openEditModal = (ticker) => {
    setEditorMode("edit");
    setSelectedTicker(ticker);
    setTickerForm(mapTickerToForm(ticker));
    setShowEditorModal(true);
  };

  const closeEditorModal = () => {
    setShowEditorModal(false);
    setSelectedTicker(null);
    setTickerForm({ ...DEFAULT_TICKER_FORM });
    setSavingTicker(false);
  };

  const handleSaveTicker = async () => {
    const payload = buildTickerPayload(tickerForm);
    const requiredFields = [
      "symbol_value",
      "label",
      "asset_class",
      "country",
      "category",
      "yahoo_ticker",
    ];

    const missingField = requiredFields.find((field) => !payload[field]);
    if (missingField) {
      setFeedback({
        variant: "danger",
        message: "Please fill all required ticker fields before saving.",
      });
      return;
    }

    if (editorMode === "create" && payload.supported_subscription_types.length === 0) {
      setFeedback({
        variant: "danger",
        message:
          "Please provide at least one supported subscription type for new tickers.",
      });
      return;
    }

    if (
      payload.sort_order !== null &&
      Number.isNaN(payload.sort_order)
    ) {
      setFeedback({
        variant: "danger",
        message: "Sort order must be a valid number when provided.",
      });
      return;
    }

    setSavingTicker(true);

    try {
      await createOrUpdateTickerUniverseItem({
        tickerId: selectedTicker?.id,
        payload,
      });

      setFeedback({
        variant: "success",
        message:
          editorMode === "create"
            ? "Ticker universe item created successfully."
            : "Ticker universe item updated successfully.",
      });
      closeEditorModal();
      await loadTickers();
    } catch (error) {
      setFeedback({
        variant: "danger",
        message: error.message || "Unable to save ticker universe item.",
      });
    } finally {
      setSavingTicker(false);
    }
  };

  const handleStatusAction = async (ticker) => {
    const actionKey = ticker.id || ticker.symbol_value;
    setRowActionKey(actionKey);

    try {
      if (ticker.is_active) {
        await disableTickerUniverseItem({
          tickerId: ticker.id,
          symbolValue: ticker.symbol_value,
        });
        setFeedback({
          variant: "success",
          message: `${ticker.symbol_value} was disabled.`,
        });
      } else {
        await setTickerUniverseItemStatus({
          tickerId: ticker.id,
          symbolValue: ticker.symbol_value,
          isActive: true,
        });
        setFeedback({
          variant: "success",
          message: `${ticker.symbol_value} was activated.`,
        });
      }

      await loadTickers();
    } catch (error) {
      setFeedback({
        variant: "danger",
        message: error.message || "Unable to update ticker status.",
      });
    } finally {
      setRowActionKey("");
    }
  };

  const openBulkUpdateModal = () => {
    setBulkForm({
      ...DEFAULT_BULK_FORM,
      assetClass: appliedFilters.assetClass,
      country: appliedFilters.country,
      category: appliedFilters.category,
      isActive: appliedFilters.isActive,
    });
    setShowBulkModal(true);
  };

  const closeBulkUpdateModal = () => {
    setShowBulkModal(false);
    setBulkForm({ ...DEFAULT_BULK_FORM });
    setSavingBulkUpdate(false);
  };

  const handleBulkUpdate = async () => {
    const tickerIds = splitListInput(bulkForm.tickerIdsText);
    const symbolValues = splitListInput(bulkForm.symbolValuesText);
    const addSubscriptionTypes = splitListInput(bulkForm.addSubscriptionTypesText);
    const removeSubscriptionTypes = splitListInput(
      bulkForm.removeSubscriptionTypesText,
    );

    const payload = {};

    if (tickerIds.length > 0) {
      payload.ticker_ids = tickerIds;
    }
    if (symbolValues.length > 0) {
      payload.symbol_values = symbolValues;
    }
    if (bulkForm.assetClass.trim()) {
      payload.asset_class = bulkForm.assetClass.trim();
    }
    if (bulkForm.country.trim()) {
      payload.country = bulkForm.country.trim();
    }
    if (bulkForm.category.trim()) {
      payload.category = bulkForm.category.trim();
    }
    if (bulkForm.isActive !== "") {
      payload.is_active = bulkForm.isActive === "true";
    }
    if (addSubscriptionTypes.length > 0) {
      payload.add_subscription_types = addSubscriptionTypes;
    }
    if (removeSubscriptionTypes.length > 0) {
      payload.remove_subscription_types = removeSubscriptionTypes;
    }

    const hasFilter =
      Boolean(payload.asset_class) ||
      Boolean(payload.country) ||
      Boolean(payload.category) ||
      payload.is_active !== undefined ||
      Array.isArray(payload.ticker_ids) ||
      Array.isArray(payload.symbol_values);

    const hasSubscriptionChange =
      Array.isArray(payload.add_subscription_types) ||
      Array.isArray(payload.remove_subscription_types);

    if (!hasFilter) {
      setFeedback({
        variant: "danger",
        message:
          "Bulk update requires at least one filter: ids, symbols, asset class, country, category, or active status.",
      });
      return;
    }

    if (!hasSubscriptionChange) {
      setFeedback({
        variant: "danger",
        message:
          "Bulk update requires at least one subscription type to add or remove.",
      });
      return;
    }

    setSavingBulkUpdate(true);

    try {
      const results = await bulkUpdateTickerSubscriptions(payload);
      setFeedback({
        variant: "success",
        message: `Bulk update completed for ${results.updated_count || 0} ticker universe items.`,
      });
      closeBulkUpdateModal();
      await loadTickers();
    } catch (error) {
      setFeedback({
        variant: "danger",
        message: error.message || "Unable to bulk update subscriptions.",
      });
    } finally {
      setSavingBulkUpdate(false);
    }
  };

  const openPreviewUniverseModal = () => {
    setPreviewForm({
      userId: "",
      subscriptionTypesText: appliedFilters.subscriptionType || "",
    });
    setPreviewResults(null);
    setShowPreviewModal(true);
  };

  const closePreviewUniverseModal = () => {
    setShowPreviewModal(false);
    setPreviewForm({ ...DEFAULT_PREVIEW_FORM });
    setPreviewResults(null);
    setLoadingPreview(false);
  };

  const handlePreviewUniverse = async () => {
    const subscriptionTypes = splitListInput(previewForm.subscriptionTypesText);
    const userId = previewForm.userId.trim();

    if (subscriptionTypes.length === 0 && !userId) {
      setFeedback({
        variant: "danger",
        message:
          "Provide a user id or one or more subscription types to preview the ticker universe.",
      });
      return;
    }

    setLoadingPreview(true);

    try {
      const results = await previewTickerUniverse({ userId, subscriptionTypes });
      setPreviewResults(results);
    } catch (error) {
      setFeedback({
        variant: "danger",
        message: error.message || "Unable to preview ticker universe.",
      });
    } finally {
      setLoadingPreview(false);
    }
  };

  return (
    <>
      <div className="border rounded-4 bg-white shadow-sm p-3 p-lg-4 mb-3">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
          <div>
            <div
              className="text-uppercase text-muted fw-semibold mb-2"
              style={{ fontSize: "0.72rem", letterSpacing: "0.05em" }}
            >
              Admin Panel
            </div>
            <h4 className="mb-2">Ticker Management</h4>
            <div className="text-muted small" style={{ maxWidth: "42rem" }}>
              Manage ticker universe rows, adjust subscription access, and
              preview the exact frontend-ready payload without leaving the
              admin surface.
            </div>
          </div>

          <div className="d-flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={loadTickers}
              disabled={loading}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={openPreviewUniverseModal}
            >
              Preview
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={openBulkUpdateModal}
            >
              Bulk Update
            </Button>
            <Button size="sm" onClick={openCreateModal}>
              Add Ticker
            </Button>
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2 mt-3">
          <StatPill label="Visible rows" value={tickerItems.length} />
          <StatPill label="Matched total" value={totalCount} />
          <StatPill label="Active filters" value={activeFilterCount} />
          <StatPill
            label="Known subscriptions"
            value={knownSubscriptionTypes.length}
          />
        </div>
      </div>

      {feedback ? (
        <Alert
          variant={feedback.variant}
          dismissible
          onClose={() => setFeedback(null)}
        >
          {feedback.message}
        </Alert>
      ) : null}

      <Card className="border rounded-4 shadow-sm mb-3">
        <Card.Body className="p-3 p-lg-4">
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
            <div>
              <div className="fw-semibold">Filters</div>
              <div className="text-muted small">
                Keep the search tight and only pull the rows you need.
              </div>
            </div>
            <div className="text-muted small">
              {activeFilterCount === 0
                ? "No filters applied"
                : `${activeFilterCount} active filter${
                    activeFilterCount === 1 ? "" : "s"
                  }`}
            </div>
          </div>

          <Row className="g-3">
            <Col md={3}>
              <Form.Group>
                <FieldLabel>Ticker ID</FieldLabel>
                <Form.Control
                  size="sm"
                  className={INPUT_CLASSNAME}
                  value={draftFilters.tickerId}
                  onChange={(event) =>
                    updateDraftFilter("tickerId", event.target.value)
                  }
                  placeholder="uuid"
                />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <FieldLabel>Symbol Value</FieldLabel>
                <Form.Control
                  size="sm"
                  className={INPUT_CLASSNAME}
                  value={draftFilters.symbolValue}
                  onChange={(event) =>
                    updateDraftFilter("symbolValue", event.target.value)
                  }
                  placeholder="FX-USDJPY"
                />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <FieldLabel>Subscription Type</FieldLabel>
                <Form.Control
                  size="sm"
                  className={INPUT_CLASSNAME}
                  list="ticker-management-subscription-types"
                  value={draftFilters.subscriptionType}
                  onChange={(event) =>
                    updateDraftFilter("subscriptionType", event.target.value)
                  }
                  placeholder="COTRADERFX"
                />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <FieldLabel>Active Status</FieldLabel>
                <Form.Select
                  size="sm"
                  className={INPUT_CLASSNAME}
                  value={draftFilters.isActive}
                  onChange={(event) =>
                    updateDraftFilter("isActive", event.target.value)
                  }
                >
                  <option value="">All</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <FieldLabel>Asset Class</FieldLabel>
                <Form.Control
                  size="sm"
                  className={INPUT_CLASSNAME}
                  list="ticker-management-asset-classes"
                  value={draftFilters.assetClass}
                  onChange={(event) =>
                    updateDraftFilter("assetClass", event.target.value)
                  }
                  placeholder="FOREIGN_EXCHANGE"
                />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <FieldLabel>Country</FieldLabel>
                <Form.Control
                  size="sm"
                  className={INPUT_CLASSNAME}
                  list="ticker-management-countries"
                  value={draftFilters.country}
                  onChange={(event) =>
                    updateDraftFilter("country", event.target.value)
                  }
                  placeholder="GLOBAL"
                />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <FieldLabel>Category</FieldLabel>
                <Form.Control
                  size="sm"
                  className={INPUT_CLASSNAME}
                  list="ticker-management-categories"
                  value={draftFilters.category}
                  onChange={(event) =>
                    updateDraftFilter("category", event.target.value)
                  }
                  placeholder="Currencies"
                />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <FieldLabel>Page Size</FieldLabel>
                <Form.Select
                  size="sm"
                  className={INPUT_CLASSNAME}
                  value={pageSize}
                  onChange={(event) => {
                    setPageSize(Number(event.target.value));
                    setCurrentPage(1);
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option} rows
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col xs={12}>
              <div className="d-flex flex-wrap gap-2 pt-1">
                <Button size="sm" onClick={applyFilters}>
                  Apply Filters
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={resetFilters}
                >
                  Reset
                </Button>
              </div>
            </Col>
          </Row>

          <datalist id="ticker-management-subscription-types">
            {knownSubscriptionTypes.map((subscriptionType) => (
              <option key={subscriptionType} value={subscriptionType} />
            ))}
          </datalist>
          <datalist id="ticker-management-asset-classes">
            {assetClassSuggestions.map((assetClass) => (
              <option key={assetClass} value={assetClass} />
            ))}
          </datalist>
          <datalist id="ticker-management-countries">
            {countrySuggestions.map((country) => (
              <option key={country} value={country} />
            ))}
          </datalist>
          <datalist id="ticker-management-categories">
            {categorySuggestions.map((category) => (
              <option key={category} value={category} />
            ))}
          </datalist>
        </Card.Body>
      </Card>

      <div className="border rounded-4 overflow-hidden bg-white shadow-sm">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 px-3 px-lg-4 py-3 border-bottom bg-light">
          <div>
            <div className="fw-semibold">Universe Items</div>
            <div className="text-muted small">
              {totalCount === 0
                ? "No rows to display"
                : `${tableStart}-${tableEnd} of ${totalCount} rows`}
            </div>
          </div>

          <div className="text-muted small">
            Dense view with status, subscription access, and quick actions.
          </div>
        </div>

        <div className="table-responsive">
          <Table hover className="mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th>Ticker</th>
                <th>Scope</th>
                <th>Subscriptions</th>
                <th>Status</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-5">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading ticker universe items...
                  </td>
                </tr>
              ) : tickerItems.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-5 text-muted">
                    No ticker universe items matched the current filters.
                  </td>
                </tr>
              ) : (
                tickerItems.map((ticker) => {
                  const actionKey = ticker.id || ticker.symbol_value;
                  const rowBusy = rowActionKey === actionKey;

                  return (
                    <tr key={actionKey}>
                      <td style={{ minWidth: "15rem" }}>
                        <div className="fw-semibold">
                          {ticker.symbol_value || "—"}
                        </div>
                        <div className="text-muted small">
                          {ticker.label || "—"}
                        </div>
                        <div
                          className="text-muted"
                          style={{ fontSize: "0.72rem" }}
                        >
                          {ticker.yahoo_ticker || "—"}
                        </div>
                      </td>
                      <td style={{ minWidth: "12rem" }}>
                        <div className="small fw-medium">
                          {formatAssetLabel(ticker.asset_class) || "—"}
                        </div>
                        <div className="text-muted small">
                          {ticker.country || "—"}
                          {ticker.category ? ` • ${ticker.category}` : ""}
                        </div>
                        <div
                          className="text-muted"
                          style={{ fontSize: "0.72rem" }}
                        >
                          Sort {ticker.sort_order ?? "—"}
                        </div>
                      </td>
                      <td>
                        <SubscriptionBadges
                          values={ticker.supported_subscription_types}
                        />
                      </td>
                      <td>
                        <div className="d-flex flex-column gap-1">
                          <div>
                            <Badge
                              bg={ticker.is_active ? "success" : "secondary"}
                            >
                              {ticker.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </div>
                          <div
                            className="text-muted"
                            style={{ fontSize: "0.72rem" }}
                          >
                            {ticker.id
                              ? `${ticker.id.slice(0, 8)}...`
                              : "No ID"}
                          </div>
                        </div>
                      </td>
                      <td>{formatDateTime(ticker.updated_at)}</td>
                      <td>
                        <div className="d-flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => openEditModal(ticker)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant={
                              ticker.is_active
                                ? "outline-danger"
                                : "outline-success"
                            }
                            onClick={() => handleStatusAction(ticker)}
                            disabled={rowBusy}
                          >
                            {rowBusy ? (
                              <>
                                <Spinner
                                  animation="border"
                                  size="sm"
                                  className="me-2"
                                />
                                Saving
                              </>
                            ) : ticker.is_active ? (
                              "Disable"
                            ) : (
                              "Activate"
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>

        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 p-3 border-top bg-light">
          <div className="text-muted small">
            Showing {tableStart}-{tableEnd} of {totalCount} ticker universe
            items
          </div>

          <div className="d-flex flex-wrap align-items-center gap-2">
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              disabled={currentPage === 1 || loading}
            >
              Previous
            </Button>
            <span className="small text-muted">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() =>
                setCurrentPage((page) => Math.min(totalPages, page + 1))
              }
              disabled={currentPage >= totalPages || loading}
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      <Modal show={showEditorModal} onHide={closeEditorModal} size="xl" centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <div>
            <Modal.Title>
              {editorMode === "create" ? "Add Ticker" : "Edit Ticker"}
            </Modal.Title>
            <div className="text-muted small mt-1">
              Keep the metadata tight so the frontend universe stays clean.
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="rounded-3 border bg-light p-3 mb-3">
            <div className="fw-semibold small mb-1">What belongs here</div>
            <div className="text-muted small mb-0">
              Use one row per ticker universe item, with subscription types
              entered as comma- or newline-separated values.
            </div>
          </div>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <FieldLabel required>Symbol Value</FieldLabel>
                <Form.Control
                  size="sm"
                  className={INPUT_CLASSNAME}
                  value={tickerForm.symbol_value}
                  onChange={(event) =>
                    updateTickerForm("symbol_value", event.target.value)
                  }
                  placeholder="FX-USDJPY"
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <FieldLabel required>Label</FieldLabel>
                <Form.Control
                  size="sm"
                  className={INPUT_CLASSNAME}
                  value={tickerForm.label}
                  onChange={(event) =>
                    updateTickerForm("label", event.target.value)
                  }
                  placeholder="USD/JPY"
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <FieldLabel required>Asset Class</FieldLabel>
                <Form.Control
                  size="sm"
                  className={INPUT_CLASSNAME}
                  list="ticker-management-asset-classes"
                  value={tickerForm.asset_class}
                  onChange={(event) =>
                    updateTickerForm("asset_class", event.target.value)
                  }
                  placeholder="FOREIGN_EXCHANGE"
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <FieldLabel required>Country</FieldLabel>
                <Form.Control
                  size="sm"
                  className={INPUT_CLASSNAME}
                  list="ticker-management-countries"
                  value={tickerForm.country}
                  onChange={(event) =>
                    updateTickerForm("country", event.target.value)
                  }
                  placeholder="GLOBAL"
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <FieldLabel required>Category</FieldLabel>
                <Form.Control
                  size="sm"
                  className={INPUT_CLASSNAME}
                  list="ticker-management-categories"
                  value={tickerForm.category}
                  onChange={(event) =>
                    updateTickerForm("category", event.target.value)
                  }
                  placeholder="Currencies"
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <FieldLabel required>Yahoo Ticker</FieldLabel>
                <Form.Control
                  size="sm"
                  className={INPUT_CLASSNAME}
                  value={tickerForm.yahoo_ticker}
                  onChange={(event) =>
                    updateTickerForm("yahoo_ticker", event.target.value)
                  }
                  placeholder="USDJPY=X"
                />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <FieldLabel>Sort Order</FieldLabel>
                <Form.Control
                  size="sm"
                  className={INPUT_CLASSNAME}
                  type="number"
                  value={tickerForm.sort_order}
                  onChange={(event) =>
                    updateTickerForm("sort_order", event.target.value)
                  }
                  placeholder="10"
                />
              </Form.Group>
            </Col>

            <Col md={3} className="d-flex align-items-end">
              <Form.Check
                type="switch"
                id="ticker-management-active-switch"
                label="Ticker is active"
                checked={tickerForm.is_active}
                onChange={(event) =>
                  updateTickerForm("is_active", event.target.checked)
                }
              />
            </Col>

            <Col xs={12}>
              <Form.Group>
                <FieldLabel required>Supported Subscription Types</FieldLabel>
                <Form.Control
                  size="sm"
                  className={INPUT_CLASSNAME}
                  as="textarea"
                  rows={2}
                  value={tickerForm.supported_subscription_types_text}
                  onChange={(event) =>
                    updateTickerForm(
                      "supported_subscription_types_text",
                      event.target.value,
                    )
                  }
                  placeholder="Comma or newline separated, e.g. COTRADERFULL, COTRADERFX"
                />
                <Form.Text className="text-muted">
                  Comma or newline separated.
                  {knownSubscriptionTypes.length > 0
                    ? " Known subscription types are shown below."
                    : ""}
                </Form.Text>
                {parsedEditorSubscriptionTypes.length > 0 ? (
                  <div className="mt-2">
                    <SubscriptionBadges values={parsedEditorSubscriptionTypes} />
                  </div>
                ) : null}
                {knownSubscriptionTypes.length > 0 ? (
                  <div className="mt-2">
                    <SubscriptionBadges values={knownSubscriptionTypes} />
                  </div>
                ) : null}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <FieldLabel>UDF1</FieldLabel>
                <Form.Control
                  size="sm"
                  className={INPUT_CLASSNAME}
                  value={tickerForm.udf1}
                  onChange={(event) =>
                    updateTickerForm("udf1", event.target.value)
                  }
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <FieldLabel>UDF2</FieldLabel>
                <Form.Control
                  size="sm"
                  className={INPUT_CLASSNAME}
                  value={tickerForm.udf2}
                  onChange={(event) =>
                    updateTickerForm("udf2", event.target.value)
                  }
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <FieldLabel>UDF3</FieldLabel>
                <Form.Control
                  size="sm"
                  className={INPUT_CLASSNAME}
                  value={tickerForm.udf3}
                  onChange={(event) =>
                    updateTickerForm("udf3", event.target.value)
                  }
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <FieldLabel>UDF4</FieldLabel>
                <Form.Control
                  size="sm"
                  className={INPUT_CLASSNAME}
                  value={tickerForm.udf4}
                  onChange={(event) =>
                    updateTickerForm("udf4", event.target.value)
                  }
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button size="sm" variant="outline-secondary" onClick={closeEditorModal}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSaveTicker} disabled={savingTicker}>
            {savingTicker ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : editorMode === "create" ? (
              "Create Ticker"
            ) : (
              "Save Changes"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showBulkModal} onHide={closeBulkUpdateModal} size="lg" centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <div>
            <Modal.Title>Bulk Update Ticker Subscriptions</Modal.Title>
            <div className="text-muted small mt-1">
              Use a narrow filter, then add or remove subscription access in
              one pass.
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="light" className="border">
            Add or remove subscription types across multiple tickers. At least
            one filter and one subscription change are required.
          </Alert>

          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <FieldLabel>Ticker IDs</FieldLabel>
                <Form.Control
                  size="sm"
                  className={INPUT_CLASSNAME}
                  as="textarea"
                  rows={2}
                  value={bulkForm.tickerIdsText}
                  onChange={(event) =>
                    updateBulkForm("tickerIdsText", event.target.value)
                  }
                  placeholder="Comma or newline separated UUIDs"
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <FieldLabel>Symbol Values</FieldLabel>
                <Form.Control
                  size="sm"
                  className={INPUT_CLASSNAME}
                  as="textarea"
                  rows={2}
                  value={bulkForm.symbolValuesText}
                  onChange={(event) =>
                    updateBulkForm("symbolValuesText", event.target.value)
                  }
                  placeholder="Comma or newline separated symbols"
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <FieldLabel>Asset Class</FieldLabel>
                <Form.Control
                  size="sm"
                  className={INPUT_CLASSNAME}
                  list="ticker-management-asset-classes"
                  value={bulkForm.assetClass}
                  onChange={(event) =>
                    updateBulkForm("assetClass", event.target.value)
                  }
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <FieldLabel>Country</FieldLabel>
                <Form.Control
                  size="sm"
                  className={INPUT_CLASSNAME}
                  list="ticker-management-countries"
                  value={bulkForm.country}
                  onChange={(event) =>
                    updateBulkForm("country", event.target.value)
                  }
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <FieldLabel>Category</FieldLabel>
                <Form.Control
                  size="sm"
                  className={INPUT_CLASSNAME}
                  list="ticker-management-categories"
                  value={bulkForm.category}
                  onChange={(event) =>
                    updateBulkForm("category", event.target.value)
                  }
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <FieldLabel>Active Status</FieldLabel>
                <Form.Select
                  size="sm"
                  className={INPUT_CLASSNAME}
                  value={bulkForm.isActive}
                  onChange={(event) =>
                    updateBulkForm("isActive", event.target.value)
                  }
                >
                  <option value="">Ignore status</option>
                  <option value="true">Only active</option>
                  <option value="false">Only inactive</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <FieldLabel>Add Subscription Types</FieldLabel>
                <Form.Control
                  size="sm"
                  className={INPUT_CLASSNAME}
                  as="textarea"
                  rows={2}
                  value={bulkForm.addSubscriptionTypesText}
                  onChange={(event) =>
                    updateBulkForm(
                      "addSubscriptionTypesText",
                      event.target.value,
                    )
                  }
                  placeholder="Comma or newline separated"
                />
                {parsedBulkAddSubscriptionTypes.length > 0 ? (
                  <div className="mt-2">
                    <SubscriptionBadges
                      values={parsedBulkAddSubscriptionTypes}
                    />
                  </div>
                ) : null}
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <FieldLabel>Remove Subscription Types</FieldLabel>
                <Form.Control
                  size="sm"
                  className={INPUT_CLASSNAME}
                  as="textarea"
                  rows={2}
                  value={bulkForm.removeSubscriptionTypesText}
                  onChange={(event) =>
                    updateBulkForm(
                      "removeSubscriptionTypesText",
                      event.target.value,
                    )
                  }
                  placeholder="Comma or newline separated"
                />
                {parsedBulkRemoveSubscriptionTypes.length > 0 ? (
                  <div className="mt-2">
                    <SubscriptionBadges
                      values={parsedBulkRemoveSubscriptionTypes}
                    />
                  </div>
                ) : null}
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button size="sm" variant="outline-secondary" onClick={closeBulkUpdateModal}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleBulkUpdate} disabled={savingBulkUpdate}>
            {savingBulkUpdate ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              "Run Bulk Update"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showPreviewModal}
        onHide={closePreviewUniverseModal}
        size="xl"
        centered
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <div>
            <Modal.Title>Preview Ticker Universe</Modal.Title>
            <div className="text-muted small mt-1">
              Inspect the output exactly as the frontend will receive it.
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3 mb-3">
            <Col md={6}>
              <Form.Group>
                <FieldLabel>User ID</FieldLabel>
                <Form.Control
                  size="sm"
                  className={INPUT_CLASSNAME}
                  value={previewForm.userId}
                  onChange={(event) =>
                    updatePreviewForm("userId", event.target.value)
                  }
                  placeholder="User UUID"
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <FieldLabel>Subscription Types</FieldLabel>
                <Form.Control
                  size="sm"
                  className={INPUT_CLASSNAME}
                  as="textarea"
                  rows={2}
                  value={previewForm.subscriptionTypesText}
                  onChange={(event) =>
                    updatePreviewForm(
                      "subscriptionTypesText",
                      event.target.value,
                    )
                  }
                  placeholder="Comma or newline separated"
                />
                <Form.Text className="text-muted">
                  If subscription types are provided, they override the user id.
                </Form.Text>
                {parsedPreviewSubscriptionTypes.length > 0 ? (
                  <div className="mt-2">
                    <SubscriptionBadges
                      values={parsedPreviewSubscriptionTypes}
                    />
                  </div>
                ) : null}
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex justify-content-end mb-3">
            <Button size="sm" onClick={handlePreviewUniverse} disabled={loadingPreview}>
              {loadingPreview ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading Preview...
                </>
              ) : (
                "Load Preview"
              )}
            </Button>
          </div>

          {previewResults ? (
            <>
              <Card className="border-0 bg-light mb-3">
                <Card.Body>
                  <Row className="g-3">
                    <Col md={4}>
                      <div className="text-muted small mb-1">
                        Effective Subscription Types
                      </div>
                      <SubscriptionBadges
                        values={previewResults.subscription_types || []}
                      />
                    </Col>

                    <Col md={4}>
                      <div className="text-muted small mb-1">Preview User</div>
                      <div>{previewResults.user_id || "Explicit subscription preview"}</div>
                    </Col>

                    <Col md={4}>
                      <div className="text-muted small mb-1">
                        Total Preview Options
                      </div>
                      <div>
                        {countPreviewOptions(
                          previewResults.ticker_universe?.options || {},
                        )}
                      </div>
                    </Col>

                    <Col xs={12}>
                      <div className="text-muted small mb-1">
                        Default Selection
                      </div>
                      <div className="fw-medium">
                        {previewResults.ticker_universe?.default_selection
                          ? `${previewResults.ticker_universe.default_selection.asset_type || "—"} / ${
                              previewResults.ticker_universe.default_selection.country || "—"
                            } / ${
                              previewResults.ticker_universe.default_selection.category || "—"
                            } / ${
                              previewResults.ticker_universe.default_selection.value || "—"
                            }`
                          : "—"}
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {previewAssetCounts.length > 0 ? (
                <div className="table-responsive mb-3">
                  <Table bordered size="sm" className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Asset Type</th>
                        <th>Option Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewAssetCounts.map((asset) => (
                        <tr key={asset.assetType}>
                          <td>{formatAssetLabel(asset.assetType)}</td>
                          <td>{asset.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : null}

              <Form.Group>
                <FieldLabel>Raw Preview Payload</FieldLabel>
                <Form.Control
                  size="sm"
                  className={INPUT_CLASSNAME}
                  as="textarea"
                  rows={16}
                  readOnly
                  value={JSON.stringify(
                    previewResults.ticker_universe || {},
                    null,
                    2,
                  )}
                />
              </Form.Group>
            </>
          ) : (
            <div className="text-muted text-center py-4">
              Run a preview to inspect the frontend-ready ticker universe output.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={closePreviewUniverseModal}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default TickerManagement;
